import { newPasswordSchema, routes } from '@pmai/contracts';
import { Alert, Button, TextField } from '@pmai/ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useApiMutation } from '../../shared/api/hooks';
import { applyFieldErrors, bind, useZodForm } from '../../shared/forms/form';
import { useErrorText } from '../../shared/i18n';
import { AuthCard, BackToLogin, NewPasswordField, useTokenParam } from './auth-layout';

/** FR-AUTH-103: one-time link → new password; all other sessions end. */
export function ResetPasswordPage() {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const token = useTokenParam();
  const schema = useMemo(
    () =>
      z
        .object({ password: newPasswordSchema, confirm: z.string() })
        .refine((v) => v.password === v.confirm, { path: ['confirm'], message: t('auth.reset.mismatch') }),
    [t],
  );
  const form = useZodForm(schema, { defaultValues: { password: '', confirm: '' } });
  const reset = useApiMutation(routes.auth.passwordResetConfirm, {
    onError: (error) => applyFieldErrors(form, error),
  });

  if (!token) {
    return (
      <AuthCard title={t('auth.reset.title')} footer={<BackToLogin />}>
        <Alert tone="danger">{t('auth.missingToken')}</Alert>
      </AuthCard>
    );
  }

  return (
    <AuthCard title={t('auth.reset.title')} footer={<BackToLogin />}>
      {reset.isSuccess ? (
        <Alert tone="success">{t('auth.reset.done')}</Alert>
      ) : (
        <form
          noValidate
          className="flex flex-col gap-4"
          onSubmit={form.handleSubmit(({ password }) => reset.mutate({ body: { token, password } }))}
        >
          {reset.error && <Alert tone="danger">{errorText(reset.error)}</Alert>}
          <NewPasswordField label={t('auth.reset.newPassword')} {...bind(form, 'password')} />
          <TextField
            label={t('auth.reset.confirm')}
            type="password"
            autoComplete="new-password"
            required
            {...bind(form, 'confirm')}
          />
          <Button type="submit" loading={reset.isPending}>
            {t('auth.reset.submit')}
          </Button>
        </form>
      )}
    </AuthCard>
  );
}
